import { DropboxFilesGridView } from '@components/layout/grids';
import {
    useFirmwareFavoriteMutation,
    useFirmwareUnfavoriteMutation,
    useLazyGetFirmwareDropboxFilesLoadedPathsQuery,
} from '@firmwares/services';
import { useAuth } from '@hooks/useAuth';
import { useCallback } from 'react';
import { defineMessage } from 'react-intl';

export const FirmwaresGridView = () => {
    const { isLoggedIn } = useAuth();
    const [triggerLoadedPaths] = useLazyGetFirmwareDropboxFilesLoadedPathsQuery();
    const [favoriteFile] = useFirmwareFavoriteMutation();
    const [unfavoriteFile] = useFirmwareUnfavoriteMutation();

    const loadPage = useCallback(
        async ({
            queryPath,
            pageSize,
            loadedPaths,
        }: {
            queryPath: string;
            pageSize: number;
            loadedPaths: string[];
        }) => {
            const pageData = await triggerLoadedPaths(
                {
                    path: queryPath,
                    page_size: pageSize,
                    loaded_paths: loadedPaths,
                },
                true
            ).unwrap();

            return {
                results: pageData.results ?? [],
                count: pageData.count,
            };
        },
        [triggerLoadedPaths]
    );

    const handleFavorite = useCallback(
        async ({ path, folderPath }: { path: string; folderPath: string }) => {
            await favoriteFile({ path, folderPath }).unwrap();
        },
        [favoriteFile]
    );

    const handleUnfavorite = useCallback(
        async ({ path, folderPath }: { path: string; folderPath: string }) => {
            await unfavoriteFile({ path, folderPath }).unwrap();
        },
        [unfavoriteFile]
    );

    return (
        <DropboxFilesGridView
            routeSegment="firmware"
            downloadType="firmwares"
            loadErrorMessage={defineMessage({
                id: 'firmwares.loadError',
                defaultMessage: 'Error loading firmwares',
            })}
            loadFailedMessage={defineMessage({
                id: 'firmwares.loadFailed',
                defaultMessage: 'Failed to load firmwares.',
            })}
            emptyListMessage={defineMessage({
                id: 'firmwares.noAssets',
                defaultMessage: 'No firmwares found in this folder.',
            })}
            loadPage={loadPage}
            onFavorite={isLoggedIn ? handleFavorite : undefined}
            onUnfavorite={isLoggedIn ? handleUnfavorite : undefined}
        />
    );
};
